import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { EnhancedDeal, RfqPackage, RfqProject, TimberProject } from '../../types';
import type { PurchaseJobPlBucket, PurchasingPolicy } from '../../types/purchasing';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { parseQuoteTotalInput } from '../../utils/rfqQuoteTotal';
import {
  createOtherQuoteSubmission,
  ensureOtherQuoteRfqProject,
} from '../../utils/otherQuoteUpload';
import { otherQuoteVendorOrgId } from '../../utils/otherQuoteVendor';
import {
  OTHER_QUOTE_PAYMENT_METHODS,
  isOtherQuotePayMethodId,
  type OtherQuotePayMethodId,
} from '../../utils/otherQuotePurchase';
import { DEFAULT_PURCHASING_POLICY } from '../../utils/purchasingPolicy';
import { crmUsersToResolverUsers, overlaySignedInApprover } from '../../utils/purchasingApprovals';
import { subscribePurchasingPolicy } from '../../utils/purchasingPolicySettings';
import { PURCHASE_JOB_PL_BUCKETS } from '../../utils/purchasingSpendClass';
import { validateSupplierPaymentTerms, type SupplierPaymentTermsSpec } from '../../utils/supplierBills';
import SupplierPaymentTermsEditor, {
  defaultSupplierPaymentTerms,
} from './SupplierPaymentTermsEditor';
import './LegacyQuoteEditModal.css';
import './OtherQuoteUploadModal.css';
import './SupplierPaymentTermsModal.css';

export type OtherQuoteUploadSuccess = {
  submissionId: string;
  purchaseRequisitionId: string;
  vendorName: string;
  quoteLabel: string;
  amount: number;
  issued: boolean;
  paymentMethodId: OtherQuotePayMethodId;
  paymentTermsDraft?: SupplierPaymentTermsSpec;
};

export type OtherQuoteUploadModalProps = {
  open: boolean;
  onClose: () => void;
  deal: EnhancedDeal;
  timberProject?: TimberProject | null;
  project: RfqProject | null;
  packages: RfqPackage[];
  onSuccess?: (result: OtherQuoteUploadSuccess) => void;
};

function todayDateInputValue(): string {
  return new Date().toISOString().slice(0, 10);
}

const OtherQuoteUploadModal: React.FC<OtherQuoteUploadModalProps> = ({
  open,
  onClose,
  deal,
  timberProject,
  project,
  packages,
  onSuccess,
}) => {
  const { user } = useAuth();
  const {
    crmUsers,
    createRfqProject,
    publishRfqPackage,
    createRfqSubmission,
    updateRfqProject,
    updateRfqSubmission,
  } = useCRM();

  const [vendorName, setVendorName] = useState('');
  const [quoteLabel, setQuoteLabel] = useState('');
  const [quoteTotal, setQuoteTotal] = useState('');
  const [quoteValidUntil, setQuoteValidUntil] = useState('');
  const [receivedDate, setReceivedDate] = useState(todayDateInputValue());
  const [notes, setNotes] = useState('');
  const [plBucket, setPlBucket] = useState<PurchaseJobPlBucket | ''>('');
  const [paymentMethodId, setPaymentMethodId] = useState<OtherQuotePayMethodId | ''>('');
  const [includePaymentTerms, setIncludePaymentTerms] = useState(false);
  const [paymentTermsDraft, setPaymentTermsDraft] = useState<SupplierPaymentTermsSpec | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [policy, setPolicy] = useState<PurchasingPolicy>(DEFAULT_PURCHASING_POLICY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setVendorName('');
    setQuoteLabel('');
    setQuoteTotal('');
    setQuoteValidUntil('');
    setReceivedDate(todayDateInputValue());
    setNotes('');
    setPlBucket('');
    setPaymentMethodId('');
    setIncludePaymentTerms(false);
    setPaymentTermsDraft(null);
    setFile(null);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    return subscribePurchasingPolicy(setPolicy);
  }, [open]);

  if (!open || !deal.id) return null;

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !deal.id) {
      setError('You must be signed in to upload a quote.');
      return;
    }
    const trimmedVendor = vendorName.trim();
    if (!trimmedVendor) {
      setError('Enter a supplier name.');
      return;
    }
    if (!quoteLabel.trim()) {
      setError('Enter a quote label.');
      return;
    }
    if (!plBucket) {
      setError('Pick a proposal node.');
      return;
    }
    if (!isOtherQuotePayMethodId(paymentMethodId)) {
      setError('Choose Company Card or Bill Payment.');
      return;
    }
    if (!file) {
      setError('Attach the supplier quote PDF.');
      return;
    }
    const total = parseQuoteTotalInput(quoteTotal);
    if (!total) {
      setError('Enter a valid total quote amount.');
      return;
    }
    if (includePaymentTerms && paymentTermsDraft) {
      const termsCheck = validateSupplierPaymentTerms(paymentTermsDraft, total);
      if (!termsCheck.ok) {
        setError(termsCheck.message);
        return;
      }
    }

    setBusy(true);
    setError(null);
    try {
      const staffUserName = user.name || user.email || 'Staff';
      const ensured = await ensureOtherQuoteRfqProject(
        { createRfqProject, updateRfqProject },
        project,
        {
          dealId: deal.id,
          dealTitle: deal.title || deal.projectName,
          timberProjectId: timberProject?.id,
          timberProjectName: timberProject?.name,
          vendorName: trimmedVendor,
          staffUserId: user.id,
          staffUserName,
        },
      );

      let validUntil: Date | null = null;
      if (quoteValidUntil.trim()) {
        validUntil = new Date(`${quoteValidUntil.trim()}T23:59:59`);
        if (Number.isNaN(validUntil.getTime())) {
          throw new Error('Enter a valid quote valid-until date.');
        }
      }

      let receivedAt: Date | undefined;
      if (receivedDate.trim()) {
        receivedAt = new Date(`${receivedDate.trim()}T12:00:00`);
        if (Number.isNaN(receivedAt.getTime())) {
          throw new Error('Enter a valid received date.');
        }
      }

      const trimmedLabel = quoteLabel.trim();
      const result = await createOtherQuoteSubmission(
        {
          createRfqProject,
          publishRfqPackage,
          createRfqSubmission,
          updateRfqProject,
          updateRfqSubmission,
        },
        {
          project: ensured,
          packages,
          orgId: otherQuoteVendorOrgId(trimmedVendor),
          orgName: trimmedVendor,
          input: {
            file,
            quoteLabel: trimmedLabel,
            quoteTotal: total,
            quoteValidUntil: validUntil,
            notes: notes.trim() || undefined,
            receivedAt,
            plBucket,
            paymentMethodId,
            paymentTermsDraft: includePaymentTerms ? paymentTermsDraft ?? undefined : undefined,
          },
          staffUserId: user.id,
          staffUserName,
          dealTitle: deal.title || deal.projectName,
          timberProjectId: timberProject?.id,
          projectManagerUserId: timberProject?.projectManager,
          users: overlaySignedInApprover(
            crmUsersToResolverUsers(crmUsers),
            user.id
              ? {
                  uid: user.id,
                  displayName: staffUserName,
                  title: crmUsers.find(u => u.uid === user.id)?.title,
                  role: user.role,
                  designations: user.designations,
                  executiveBriefing: user.executiveBriefing,
                }
              : null,
          ),
          policy,
        },
      );

      onSuccess?.({
        submissionId: result.submissionId,
        purchaseRequisitionId: result.purchaseRequisitionId,
        vendorName: trimmedVendor,
        quoteLabel: trimmedLabel,
        amount: total,
        issued: result.issued,
        paymentMethodId: result.paymentMethodId,
        paymentTermsDraft: includePaymentTerms ? paymentTermsDraft ?? undefined : undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload quote.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lqi-modal-backdrop" role="presentation" onClick={handleClose}>
      <div
        className={`lqi-modal oqu-modal${includePaymentTerms ? ' oqu-modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="other-quote-upload-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lqi-modal-head">
          <div>
            <h2 id="other-quote-upload-title">Upload other quote</h2>
            <p className="lqi-modal-sub">
              Add a supplier PDF and a linked job purchase order. The quote stays awaiting approval
              until you approve it on this panel (or the PO is issued from Purchasing). Company Card
              stays on Purchasing; Bill Payment lands on Billing & Payables after issue.
            </p>
          </div>
          <button type="button" className="lqi-modal-close" aria-label="Close" onClick={handleClose}>
            <X size={16} />
          </button>
        </header>

        <form className="lqi-modal-body oqu-form" noValidate onSubmit={(e) => void handleSubmit(e)}>
          <label className="lqi-field">
            <span>Supplier</span>
            <input
              type="text"
              value={vendorName}
              disabled={busy}
              placeholder="e.g. Simpson Strong-Tie, local freight carrier"
              autoFocus
              onChange={(e) => setVendorName(e.target.value)}
            />
          </label>

          <label className="lqi-field">
            <span>Quote label</span>
            <input
              type="text"
              value={quoteLabel}
              disabled={busy}
              placeholder="e.g. Steel connectors, freight, subcontractor"
              onChange={(e) => setQuoteLabel(e.target.value)}
            />
          </label>

          <div className="oqu-form-row">
            <label className="lqi-field">
              <span>Quote total ($)</span>
              <input
                type="text"
                inputMode="decimal"
                value={quoteTotal}
                disabled={busy}
                placeholder="0"
                onChange={(e) => setQuoteTotal(e.target.value)}
              />
            </label>

            <label className="lqi-field">
              <span>Valid until (optional)</span>
              <input
                type="date"
                value={quoteValidUntil}
                disabled={busy}
                onChange={(e) => setQuoteValidUntil(e.target.value)}
              />
            </label>

            <label className="lqi-field">
              <span>Received date</span>
              <input
                type="date"
                value={receivedDate}
                disabled={busy}
                onChange={(e) => setReceivedDate(e.target.value)}
              />
            </label>
          </div>

          <div className="oqu-form-row oqu-form-row--two">
            <label className="lqi-field">
              <span>Proposal node</span>
              <select
                value={plBucket}
                disabled={busy}
                onChange={(e) => setPlBucket(e.target.value as PurchaseJobPlBucket | '')}
              >
                <option value="">Select node…</option>
                {PURCHASE_JOB_PL_BUCKETS.map(bucket => (
                  <option key={bucket.id} value={bucket.id}>
                    {bucket.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="lqi-field">
              <span>Pay method</span>
              <select
                value={paymentMethodId}
                disabled={busy}
                onChange={(e) => setPaymentMethodId(e.target.value as OtherQuotePayMethodId | '')}
              >
                <option value="">Select method…</option>
                {OTHER_QUOTE_PAYMENT_METHODS.map(method => (
                  <option key={method.id} value={method.id}>
                    {method.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="lqi-field">
            <span>Notes (optional)</span>
            <textarea
              rows={2}
              value={notes}
              disabled={busy}
              placeholder="Scope details, lead time, exclusions…"
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          <label className="lqi-field">
            <span>Quote PDF</span>
            <input
              type="file"
              accept=".pdf,application/pdf"
              disabled={busy}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file && <span className="lqi-file-name">{file.name}</span>}
          </label>

          <label className="lqi-check">
            <input
              type="checkbox"
              checked={includePaymentTerms}
              disabled={busy}
              onChange={(e) => {
                const checked = e.target.checked;
                setIncludePaymentTerms(checked);
                setPaymentTermsDraft(checked ? defaultSupplierPaymentTerms() : null);
              }}
            />
            <span>Add payment terms (optional)</span>
          </label>

          {includePaymentTerms && (
            <section className="oqu-payment-terms" aria-label="Payment terms">
              <p className="oqu-payment-terms-hint">
                Saved with the quote and copied onto the Billing & Payables bill when this Bill
                Payment PO is issued.
              </p>
              <SupplierPaymentTermsEditor
                billAmount={parseQuoteTotalInput(quoteTotal) ?? 0}
                projectId={timberProject?.id}
                value={paymentTermsDraft}
                onChange={setPaymentTermsDraft}
                onScheduleAmountChange={(amount) => setQuoteTotal(String(amount))}
                disabled={busy}
                compact
              />
            </section>
          )}

          {error && <p className="lqi-error">{error}</p>}

          <div className="lqi-modal-actions">
            <button type="button" className="ops-hide-completed-btn" disabled={busy} onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="ops-view-btn active" disabled={busy}>
              {busy ? 'Uploading…' : 'Upload quote'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OtherQuoteUploadModal;
